import {
  DragTarget,
  isOrderedDragTarget,
  ItemInstance,
} from '@headless-tree/core';
import { useCallback } from 'react';
import { ID } from 'shared/types/id';

import { InternalTreeItem, TreeItem, TreeItemID, TreeItemMove } from './types';

export const useOnDrop = (
  items: Record<TreeItemID, InternalTreeItem>,
  callback?: (affectedItems: TreeItem[], moves: TreeItemMove[]) => void,
) =>
  useCallback(
    (
      draggedItems: ItemInstance<InternalTreeItem>[],
      target: DragTarget<InternalTreeItem>,
    ) => {
      // no callback means no calculations needed
      if (!callback) {
        return;
      }

      const parentItem = target.item;
      if (!parentItem) {
        return;
      }

      // Get sourceId from parent (path-based ID needs to be converted back)
      const parentInternalItem = parentItem.getItemData();
      const newParentSourceId = parentInternalItem.sourceId;

      // Create a map of original items for lookups
      const originalItemsMap = new Map<ID, TreeItem>();
      Object.values(items).forEach((internalItem) => {
        if (!originalItemsMap.has(internalItem.sourceId)) {
          // Create original TreeItem from InternalTreeItem
          originalItemsMap.set(internalItem.sourceId, {
            id: internalItem.sourceId,
            name: internalItem.name,
            type: internalItem.type,
            children: internalItem.children?.map((childPathId) => {
              const childItem = items[childPathId];
              return childItem?.sourceId ?? childPathId;
            }),
            isFolder: internalItem.isFolder,
          });
        }
      });

      const newItems = structuredClone(Object.fromEntries(originalItemsMap));
      const targetParent = newItems[newParentSourceId];
      if (!targetParent) {
        return;
      }

      // Get sourceIds from dragged items
      const draggedSourceIds = draggedItems.map(
        (i) => i.getItemData().sourceId,
      );
      const affectedItemsMap = new Map<ID, TreeItem>();
      const moves: TreeItemMove[] = [];

      const markAffected = (id: ID, item: TreeItem) => {
        newItems[id] = item;
        affectedItemsMap.set(id, item);
      };

      // determine index to drop item
      const currentChildrenCount = targetParent.children?.length ?? 0;
      const finalIndex = isOrderedDragTarget(target)
        ? target.insertionIndex
        : currentChildrenCount;

      // remove from Old Parent
      draggedItems.forEach((draggedInstance) => {
        const childSourceId = draggedInstance.getItemData().sourceId;
        const oldParentInstance = draggedInstance.getParent();
        const oldParentInternalItem = oldParentInstance?.getItemData();
        const oldParentSourceId = oldParentInternalItem?.sourceId;

        if (oldParentSourceId && newItems[oldParentSourceId]) {
          const oldParent = newItems[oldParentSourceId];
          const newChildren = (oldParent.children ?? []).filter(
            (id) => id !== childSourceId,
          );

          markAffected(oldParentSourceId, {
            ...oldParent,
            children: newChildren,
          });
        }

        moves.push({
          itemId: childSourceId,
          fromParentId: oldParentSourceId,
          toParentId: newParentSourceId,
        });

        if (newItems[childSourceId]) {
          affectedItemsMap.set(childSourceId, newItems[childSourceId]);
        }
      });

      // add to new parent
      const freshTargetParent = newItems[newParentSourceId];
      if (freshTargetParent) {
        const newChildren = [...(freshTargetParent.children ?? [])];
        const safeIndex = Math.min(finalIndex, newChildren.length);
        newChildren.splice(safeIndex, 0, ...draggedSourceIds);

        markAffected(newParentSourceId, {
          ...freshTargetParent,
          children: newChildren,
        });
      }

      callback(Array.from(affectedItemsMap.values()), moves);
    },
    [items, callback],
  );
